import React, { useState, useEffect } from "react";
import { Plus, Search } from "lucide-react";
import { Button } from "../../shadcn/components/ui/button";
import Drawer from "../../Components/Common/DrawerLayout/Drawer";
import TextFieldRenderer from "../../Components/Form/TextFieldRenderer";
import {
  REGISTER_USER_SA,
  SEARCH_USER_WITH_IDENTITY_SA,
} from "./SuperAdminService";
import { useTranslation } from "../../context/Internationalization";
import useGraphql from "../../Hooks/useGraphql";
import useLog from "../../Hooks/useLog";
import { getStorage } from "../../auth/utils";
import { USER_ROLES } from "../../utility/Enum";
import {
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../shadcn/components/ui/sheet";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../shadcn/components/ui/card";
import { cn } from "../../shadcn/lib/utils";
import { Item, ItemContent, ItemDescription, ItemTitle } from "../../shadcn/components/ui/item";
import { toast } from "sonner";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeMobileIdentity = (value) => {
  const normalizedMobile = value.replace(/\s/g, "").replace(/\+/g, "");
  const digitsOnly = normalizedMobile.replace(/\D/g, "");

  if (digitsOnly.length === 10 && digitsOnly.startsWith("69")) {
    return `30${digitsOnly}`;
  }

  if (digitsOnly.length === 11 && digitsOnly.startsWith("069")) {
    return `30${digitsOnly.slice(1)}`;
  }

  if (digitsOnly.length === 8 && digitsOnly.startsWith("9")) {
    return `357${digitsOnly}`;
  }

  return normalizedMobile;
};

const normalizeIdentity = (value) => {
  const trimmedValue = value?.trim() || "";

  if (trimmedValue.includes("@")) {
    return trimmedValue.toLowerCase();
  }

  return normalizeMobileIdentity(trimmedValue);
};

const isValidEmail = (identity) => emailRegex.test(identity);

const isValidIdentity = (identity) => {
  if (!identity) {
    return false;
  }

  if (identity.includes("@")) {
    return isValidEmail(identity);
  }

  return identity.replace(/\D/g, "").length > 5;
};

const isImpersonating = Boolean(getStorage().getItem("impersonate"));

const AddUserToClubDrawer = ({
  openDrawer,
  onCloseDrawer,
  selectedClub,
  getClubs,
}) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({ id: "", identity: "" });
  const [user, setUser] = useState();
  const [identityError, setIdentityError] = useState("");
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();

  useEffect(() => {
    const normalizedIdentity = normalizeIdentity(filters.identity);

    if (isValidEmail(normalizedIdentity)) {
      setIdentityError("");
      resolveUser(normalizedIdentity);
    } else if (
      !normalizedIdentity.includes("@") &&
      normalizedIdentity.length > 5
    ) {
      setIdentityError("");
      resolveUser(normalizedIdentity);
    } else {
      setUser(null);
    }
  }, [filters.identity]);

  const resolveUser = (identity = normalizeIdentity(filters.identity)) => {
    if (identity) {
      axiosGraphQL({
        query: SEARCH_USER_WITH_IDENTITY_SA,
        variables: { identity },
      })
        .then((response) => {
          setUser(
            response?.data?.data?.searchUserWithIdentitySA &&
              response?.data?.data?.searchUserWithIdentitySA[0]
          );
        })
        .catch((error) => {
          errorLog(
            error,
            "AddUserToClubDrawer",
            "resolveUser",
            "SEARCH_USER_WITH_IDENTITY_SA"
          );
          toast.error(error.message);
        });
    }
  };

  const searchUser = () => {
    const normalizedIdentity = normalizeIdentity(filters.identity);

    if (!isValidIdentity(normalizedIdentity)) {
      setIdentityError(t("enter_valid_email_or_mobile"));
      toast.error(t("enter_valid_email_or_mobile"));
      return;
    }

    setIdentityError("");
    resolveUser(normalizedIdentity);
  };

  const handleSubmit = () => {
    axiosGraphQL({
      query: REGISTER_USER_SA,
      variables: {
        firstName: user?.firstName,
        lastName: user?.lastName,
        mobile: user?.mobile,
        email: user?.email,
        role: USER_ROLES.CLUB_ADMIN,
        clubId: selectedClub?._id,
      },
    })
      .then(async () => {
        toast.success(t("club_admin_successfully_created"));
        getClubs();
        onCloseDrawer();
      })
      .catch((error) => {
        errorLog(
          error,
          "AddUserToClubDrawer",
          "handleSubmit",
          "REGISTER_USER_SA"
        );
        toast.error(error.message);
      });
  };

  const onChangeFilter = ({ target: { name, value } }) => {
    setIdentityError("");
    setFilters((prevState) => ({ ...prevState, [name]: value }));
  };

  return (
    <Drawer
      open={openDrawer}
      onClose={onCloseDrawer}
      size="compact"
      aria-labelledby="add-user-to-club-drawer-title"
      closeLabel={t("close")}
    >
      <div className={cn("flex min-h-0 w-full flex-1 flex-col", isImpersonating ? "pt-6" : undefined)}>
        <SheetHeader>
          <SheetTitle id="add-user-to-club-drawer-title">{t("add_club_admin")}</SheetTitle>
          <SheetDescription>{t("add_club_admin_description")}</SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto px-6 pb-6" data-testid="add-user-to-club-drawer-body">
          <Card size="sm" role="region" aria-labelledby="find-user-section-title">
            <CardHeader>
              <CardTitle role="heading" aria-level={3} id="find-user-section-title">{t("find_user_title")}</CardTitle>
              <CardDescription>{t("find_user_description")}</CardDescription>
            </CardHeader>
            <CardContent>
            <div className="flex flex-col gap-4">
              <TextFieldRenderer
                label={t("email_or_mobile")}
                onChange={onChangeFilter}
                name={"identity"}
                value={filters.identity}
                error={identityError}
              />
              <Button type="button" className="w-full" onClick={searchUser}>
                <Search aria-hidden="true" />
                {t("search")}
              </Button>
              {user && <UserIdentitySummary user={user} />}
            </div>
          </CardContent>
          </Card>
        </div>
        <SheetFooter data-testid="add-user-to-club-drawer-footer">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button type="button" variant="outline" className="w-full sm:flex-1" onClick={onCloseDrawer}>
              {t("cancel")}
            </Button>
            <Button
              type="button"
              className="w-full sm:flex-1"
              onClick={handleSubmit}
              disabled={!user || !isValidIdentity(normalizeIdentity(filters.identity))}
            >
              <Plus aria-hidden="true" />
              {t("add")}
            </Button>
          </div>
        </SheetFooter>
      </div>
    </Drawer>
  );
};

const UserIdentitySummary = ({ user }) => {
  const identityDetails = [user?.email, user?.mobile]
    .filter(Boolean)
    .join(" • ");

  return (
    <Item variant="outline" size="sm" className="[overflow-wrap:anywhere]">
      <ItemContent>
        <ItemTitle>
          {[user?.firstName, user?.lastName].filter(Boolean).join(" ")}
        </ItemTitle>
        <ItemDescription>{identityDetails}</ItemDescription>
      </ItemContent>
    </Item>
  );
};

export default AddUserToClubDrawer;
